import nf from '@/constants/nf';
import { route } from '@/constants/routeName';
import ChildSelector from '@/components/Parent/ChildSelector';
import { familyAccountHeading, schoolDisplayName } from '@/helper/schoolLabel';
import { Paid, Person } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

export default function UserHeader({ hideChildSelector = false } = {}) {
	const user = useSelector((state) => state.auth.user);
	const selectedCustomer = useSelector((state) => state.auth.selectedCustomer);
	const selectedOutlet = useSelector((state) => state.auth.selectedOutlet);
	const parentOutlet = useSelector((state) => state.auth.parentOutlet);
	const children = useSelector((state) => state.auth.children) || [];
	const accountType = useSelector((state) => state.auth.accountType);
	const location = useLocation();

	const isParent = accountType === 'parent';
	const studentName = selectedCustomer?.member?.name;
	const studentClass = selectedCustomer?.member?.on_list_attributes?.class;
	const showSelector =
		!hideChildSelector &&
		isParent &&
		children.length > 0 &&
		!location.pathname.startsWith(`/${route.orders}`);
	const school = schoolDisplayName({ selectedOutlet, parentOutlet });

	// Legacy / student: production cream header bar on white page
	if (!isParent) {
		return (
			<div className='flex gap-2 bg-bz-default px-4 py-3'>
				<div className='relative h-16 w-14 shrink-0 border border-black'>
					{user.avatar ? (
						<img src={user.avatar} alt={user.name} className='h-full w-full object-cover' />
					) : (
						<div className='flex h-full w-full text-[48px]'>
							<Person fontSize='inherit' className='m-auto' />
						</div>
					)}
				</div>
				<div className='flex w-full items-center justify-between'>
					<div className='flex flex-col py-1 text-sm'>
						<span className='font-bold text-black'>{user.name}</span>
						<span className='text-xs text-black'>
							{[selectedOutlet?.name, studentClass].filter(Boolean).join(', ')}
						</span>
						<span
							className={twMerge(
								'text-xs font-bold text-black',
								Math.sign(+selectedCustomer?.balance) < 0 ? 'text-red-500' : ''
							)}
						>
							Deposit: {nf(+selectedCustomer?.balance || 0)}
						</span>
					</div>
					<Link
						to={`/${route.accountTopUp}`}
						className='flex flex-col items-center justify-center gap-0.5 px-2 text-center text-xs font-bold leading-tight text-black'
					>
						<Paid fontSize='medium' />
						<span>Top up</span>
					</Link>
				</div>
			</div>
		);
	}

	return (
		<div className='flex flex-col gap-2'>
			<p className='text-xs font-semibold uppercase tracking-wide text-[var(--weak)]'>
				{familyAccountHeading(school)}
			</p>
			<div className='flex gap-2'>
				<div className='relative h-12 w-12 shrink-0 border border-black'>
					{user.avatar ? (
						<img src={user.avatar} alt={user.name} className='h-full w-full object-cover' />
					) : (
						<div className='flex h-full w-full text-[36px]'>
							<Person fontSize='inherit' className='m-auto' />
						</div>
					)}
				</div>
				<div className='flex min-w-0 flex-1 flex-col justify-center gap-0.5 py-0.5 text-sm'>
					<span className='font-bold text-black'>{user.name}</span>
					{studentName ? (
						<span className='truncate text-xs text-[var(--weak)]'>
							{studentName}
							{studentClass ? `, ${studentClass}` : ''}
							{school ? ` · ${school}` : ''}
						</span>
					) : (
						<span className='text-xs text-[var(--weak)]'>
							{school || 'No child selected'}
						</span>
					)}
					{selectedCustomer && (
						<div className='flex items-center gap-3'>
							<span
								className={twMerge(
									'text-xs font-bold text-black',
									Math.sign(+selectedCustomer?.balance) < 0 ? 'text-red-500' : ''
								)}
							>
								Deposit: {nf(+selectedCustomer?.balance || 0)}
							</span>
							<Link
								to={`/${route.accountTopUp}`}
								className='inline-flex items-center gap-0.5 text-xs font-bold text-black underline underline-offset-2 transition-transform duration-100 active:scale-[0.98] active:opacity-80'
							>
								<Paid fontSize='small' /> Top up
							</Link>
						</div>
					)}
				</div>
			</div>
			{showSelector && (
				<div className='pt-0.5'>
					<ChildSelector />
				</div>
			)}
		</div>
	);
}
